import { fuelKindForType, type FuelKind } from "./catalogs";
import {
  convertFuel,
  formatFuelFigure,
  formatSignedFuel,
  fuelUsedToUsGal,
  type FuelUnit,
} from "./fuelUnits";
import { ofpMassUnit } from "./ofpTicketFuel";
import type { MissionOfpAttachment } from "./types";

/** Matches `N1_RUNNING_PCT` in sim telemetry. */
export const ENGINE_N1_RUNNING_PCT = 12;
/** Matches `APU_RUNNING_PCT` in sim telemetry. */
export const APU_ON_PCT = 50;

/** Tank vs flow may differ by this many pounds, or by this fraction, and still agree. */
export const BURN_CROSSCHECK_FLOOR_LB = 25;
export const BURN_CROSSCHECK_RATIO = 0.08;

/** Next-load tanks within this band of the shutdown snapshot are the same fuel state. */
export const TANK_LOAD_TOLERANCE_LB = 5;
export const TANK_LOAD_TOLERANCE_RATIO = 0.002;

export const NO_TRIP_FUEL_LABEL = "No trip fuel";
export const NO_BLOCK_FUEL_LABEL = "No block fuel";
export const NO_LANDING_TANKS_LABEL = "No landing tanks";
export const UNRELIABLE_FUEL_LABEL = "Unreliable";

export type BurnReason = "unlimited_fuel" | "fuel_dump" | "crosscheck";
export type BurnSource = "tank" | "flow" | "none";
export type TankLoadKind = "state_saving" | "default_reload" | "no_snapshot" | "no_quantity";
export type LiveFuelStatus = "compared" | "no_trip_fuel" | "unreliable" | "waiting";

export type OfpFuelPick = Pick<
  MissionOfpAttachment,
  "plannedTripGal" | "plannedTripLbs" | "plannedFuelGal" | "plannedFuelLbs" | "fuelPlan" | "aircraftType"
> | null | undefined;

export type FuelTankLb = { id: string; lb: number };

export type FuelShutdownSnapshot = {
  registration: string;
  atUtc: string;
  /** Canonical pounds from SimConnect. */
  fuelQtyLb: number;
  tanks: FuelTankLb[];
};

function positive(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
}

function finiteNonNeg(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * SimBrief trip fuel in canonical pounds.
 * Ramp, block, and takeoff fuel are not a substitute.
 */
export function ofpTripFuelLb(ofp: OfpFuelPick, icaoType?: string): number | null {
  if (!ofp) return null;
  const kind = fuelKindForType(icaoType || ofp.aircraftType);
  const trip = positive(ofp.fuelPlan?.trip);
  if (trip != null) {
    const mass = ofpMassUnit(ofp.fuelPlan?.units);
    if (mass) return convertFuel(trip, mass, "lb", kind);
  }
  const storedLb = positive(ofp.plannedTripLbs);
  if (storedLb != null) return storedLb;
  const storedGal = positive(ofp.plannedTripGal);
  if (storedGal != null) return convertFuel(storedGal, "gal", "lb", kind);
  return null;
}

/** Trip fuel in US gallons. Null when the OFP has no trip figure. */
export function ofpTripFuelGal(ofp: OfpFuelPick, icaoType?: string): number | null {
  if (!ofp) return null;
  const planTrip = positive(ofp.fuelPlan?.trip);
  const planMass = ofpMassUnit(ofp.fuelPlan?.units);
  if (planTrip != null && planMass) {
    const kind = fuelKindForType(icaoType || ofp.aircraftType);
    return fuelUsedToUsGal(planTrip, planMass, kind);
  }
  if (positive(ofp.plannedTripLbs) == null) {
    const storedGal = positive(ofp.plannedTripGal);
    if (storedGal != null) return storedGal;
  }
  const lb = ofpTripFuelLb(ofp, icaoType);
  if (lb == null) return null;
  const kind = fuelKindForType(icaoType || ofp.aircraftType);
  return fuelUsedToUsGal(lb, "lb", kind);
}

/**
 * SimBrief block / ramp target in canonical pounds.
 * Trip fuel is not a block target.
 */
export function ofpBlockFuelLb(ofp: OfpFuelPick, icaoType?: string): number | null {
  if (!ofp) return null;
  const kind = fuelKindForType(icaoType || ofp.aircraftType);
  const plan = ofp.fuelPlan;
  if (plan) {
    const amount = positive(plan.planRamp) ?? positive(plan.planTakeoff) ?? positive(plan.minTakeoff);
    if (amount == null) return null;
    const mass = ofpMassUnit(plan.units);
    if (!mass) return null;
    return convertFuel(amount, mass, "lb", kind);
  }
  const storedLb = positive(ofp.plannedFuelLbs);
  if (storedLb != null) return storedLb;
  const storedGal = positive(ofp.plannedFuelGal);
  if (storedGal != null) return convertFuel(storedGal, "gal", "lb", kind);
  return null;
}

export function ofpBlockFuelGal(ofp: OfpFuelPick, icaoType?: string): number | null {
  if (!ofp) return null;
  if (!ofp.fuelPlan) {
    const storedGal = positive(ofp.plannedFuelGal);
    if (storedGal != null && positive(ofp.plannedFuelLbs) == null) return storedGal;
  }
  const lb = ofpBlockFuelLb(ofp, icaoType);
  if (lb == null) return null;
  const kind = fuelKindForType(icaoType || ofp.aircraftType);
  return fuelUsedToUsGal(lb, "lb", kind);
}

export function burnsAgree(tankLb: number, flowLb: number): boolean {
  if (!Number.isFinite(tankLb) || !Number.isFinite(flowLb)) return false;
  const diff = Math.abs(tankLb - flowLb);
  const scale = Math.max(Math.abs(tankLb), Math.abs(flowLb));
  return diff <= Math.max(BURN_CROSSCHECK_FLOOR_LB, scale * BURN_CROSSCHECK_RATIO);
}

export function engineFlowPph(sample: {
  eng1FlowPph?: number | null;
  eng2FlowPph?: number | null;
  eng3FlowPph?: number | null;
  eng4FlowPph?: number | null;
}): number | null {
  const values = [sample.eng1FlowPph, sample.eng2FlowPph, sample.eng3FlowPph, sample.eng4FlowPph].filter(
    (value): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0,
  );
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0);
}

export function resolveMeasuredBurn(input: {
  tankLb: number | null;
  flowLb: number | null;
  flowSeen: boolean;
  unlimitedFuel: boolean;
  fuelDump: boolean;
}): { burnLb: number | null; reliable: boolean; reason: BurnReason | null; source: BurnSource } {
  if (input.unlimitedFuel) {
    return { burnLb: null, reliable: false, reason: "unlimited_fuel", source: "none" };
  }
  if (input.fuelDump) {
    return { burnLb: null, reliable: false, reason: "fuel_dump", source: "none" };
  }
  const tank = finiteNonNeg(input.tankLb);
  const flow = input.flowSeen ? finiteNonNeg(input.flowLb) : null;
  if (tank != null && flow != null) {
    if (!burnsAgree(tank, flow)) {
      return { burnLb: null, reliable: false, reason: "crosscheck", source: "none" };
    }
    return { burnLb: tank, reliable: true, reason: null, source: "tank" };
  }
  if (tank != null) return { burnLb: tank, reliable: true, reason: null, source: "tank" };
  if (flow != null) return { burnLb: flow, reliable: true, reason: null, source: "flow" };
  return { burnLb: null, reliable: true, reason: null, source: "none" };
}

export function burnReasonText(reason: BurnReason | null | undefined): string {
  if (reason === "unlimited_fuel") return "Unlimited fuel was on. This leg's burn is not recorded.";
  if (reason === "fuel_dump") return "A fuel dump was active. This leg's burn is not recorded.";
  if (reason === "crosscheck") return "Tank change and engine fuel flow do not agree. This leg's burn is not recorded.";
  return "";
}

/**
 * All engines off and the APU off. APU-off alone is not a shutdown.
 * A combustion slot that has been seen and then goes blank is not "off".
 * An APU var that has never been published counts as no APU.
 */
export function shutdownGate(input: {
  combustion: ReadonlyArray<boolean | null | undefined>;
  seen: ReadonlyArray<boolean>;
  n1: ReadonlyArray<number | null | undefined>;
  apuPctRpm: number | null | undefined;
  apuEverPublished: boolean;
}): { enginesOff: boolean; apuOff: boolean; ready: boolean } {
  const slots = input.combustion.map((current, index) => {
    const seen = input.seen[index] === true;
    if (current === true) return "on" as const;
    if (current === false) return "off" as const;
    if (seen) return "unknown" as const;
    return "absent" as const;
  });
  let enginesOff = false;
  if (slots.some((slot) => slot === "on" || slot === "unknown")) {
    enginesOff = false;
  } else if (slots.some((slot) => slot === "off")) {
    enginesOff = true;
  } else {
    const n1 = input.n1.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    enginesOff = n1.length > 0 && n1.every((value) => value < ENGINE_N1_RUNNING_PCT);
  }
  let apuOff = false;
  if (typeof input.apuPctRpm === "number" && Number.isFinite(input.apuPctRpm)) {
    apuOff = input.apuPctRpm < APU_ON_PCT;
  } else if (!input.apuEverPublished) {
    apuOff = true;
  }
  return { enginesOff, apuOff, ready: enginesOff && apuOff };
}

export function classifyTankLoad(
  snapshotLb: number | null | undefined,
  currentLb: number | null | undefined,
): TankLoadKind {
  if (snapshotLb == null || !Number.isFinite(snapshotLb)) return "no_snapshot";
  if (currentLb == null || !Number.isFinite(currentLb)) return "no_quantity";
  const diff = Math.abs(currentLb - snapshotLb);
  const tolerance = Math.max(TANK_LOAD_TOLERANCE_LB, Math.abs(snapshotLb) * TANK_LOAD_TOLERANCE_RATIO);
  return diff <= tolerance ? "state_saving" : "default_reload";
}

export function tankLoadText(kind: TankLoadKind | null | undefined): string {
  if (kind === "state_saving") return "State-saving load. Tanks match the last shutdown.";
  if (kind === "default_reload") return "Default reload. Tanks do not match the last shutdown.";
  return "";
}

export function nextLegUpliftLb(
  blockLb: number | null | undefined,
  landingLb: number | null | undefined,
): { status: "ok" | "no_block" | "no_baseline"; upliftLb: number | null } {
  if (blockLb == null || !Number.isFinite(blockLb) || blockLb <= 0) {
    return { status: "no_block", upliftLb: null };
  }
  if (landingLb == null || !Number.isFinite(landingLb) || landingLb < 0) {
    return { status: "no_baseline", upliftLb: null };
  }
  return { status: "ok", upliftLb: blockLb - landingLb };
}

const UPLIFT_TIP =
  "Uplift is the SimBrief block target minus the last landing tanks. It is not a full-tank assumption. Block fuel is the tank target. Trip fuel is the burn compare.";

export function upliftFigure(input: {
  blockLb: number | null | undefined;
  landingLb: number | null | undefined;
  unit: FuelUnit;
  fuel?: FuelKind;
}): { status: "ok" | "no_block" | "no_baseline"; label: string; tooltip: string; upliftLb: number | null } {
  const result = nextLegUpliftLb(input.blockLb, input.landingLb);
  if (result.status === "no_block") return { ...result, label: NO_BLOCK_FUEL_LABEL, tooltip: UPLIFT_TIP };
  if (result.status === "no_baseline" || result.upliftLb == null) {
    return { status: "no_baseline", upliftLb: null, label: NO_LANDING_TANKS_LABEL, tooltip: UPLIFT_TIP };
  }
  const kind = input.fuel ?? "jeta";
  const shown = convertFuel(result.upliftLb, "lb", input.unit, kind);
  return { status: "ok", upliftLb: result.upliftLb, label: formatFuelFigure(shown, input.unit), tooltip: UPLIFT_TIP };
}

export type FuelCompareView = {
  status: LiveFuelStatus;
  unit: FuelUnit;
  label: string;
  chip: string;
  tooltip: string;
  tripLabel: string;
  actualLabel: string;
  deltaLabel: string;
  heading: string;
};

const COMPARE_TIP =
  "Burn vs plan is actual burn minus SimBrief trip fuel. A minus means less fuel burned than the trip. A plus means more. Block and ramp fuel are the tank target, not the burn. The figure is whole pounds unless this tail or the company unit says otherwise, converted from canonical pounds. Tank decrease is cross-checked with engine fuel flow when the sim publishes it.";

export function presentFuelCompare(input: {
  tripLb: number | null;
  measured: { burnLb: number | null; reliable: boolean; reason: BurnReason | null };
  unit: FuelUnit;
  fuel?: FuelKind;
  tankLoad?: TankLoadKind | null;
  heading?: string;
}): FuelCompareView {
  const kind = input.fuel ?? "jeta";
  const unit = input.unit;
  const tripLabel =
    input.tripLb != null ? formatFuelFigure(convertFuel(input.tripLb, "lb", unit, kind), unit) : NO_TRIP_FUEL_LABEL;
  const actualLb = input.measured.reliable ? finiteNonNeg(input.measured.burnLb) : null;
  const actualLabel = !input.measured.reliable
    ? UNRELIABLE_FUEL_LABEL
    : actualLb != null
      ? formatFuelFigure(convertFuel(actualLb, "lb", unit, kind), unit)
      : "—";
  const deltaLb = input.tripLb != null && actualLb != null ? actualLb - input.tripLb : null;
  const deltaLabel = deltaLb != null ? formatSignedFuel(convertFuel(deltaLb, "lb", unit, kind), unit) : "—";
  const load = tankLoadText(input.tankLoad);
  const reason = burnReasonText(input.measured.reason);
  const extra = [reason, load].filter(Boolean).join(" ");
  const tooltip = extra ? `${COMPARE_TIP} ${extra}` : COMPARE_TIP;

  if (!input.measured.reliable) {
    return {
      status: "unreliable",
      unit,
      label: UNRELIABLE_FUEL_LABEL,
      chip: "Fuel unreliable",
      tooltip,
      tripLabel,
      actualLabel,
      deltaLabel: "—",
      heading: input.heading ?? "Burn vs plan",
    };
  }
  if (input.tripLb == null) {
    return {
      status: "no_trip_fuel",
      unit,
      label: NO_TRIP_FUEL_LABEL,
      chip: NO_TRIP_FUEL_LABEL,
      tooltip,
      tripLabel: NO_TRIP_FUEL_LABEL,
      actualLabel,
      deltaLabel: "—",
      heading: input.heading ?? "Burn vs plan",
    };
  }
  if (actualLb == null) {
    return {
      status: "waiting",
      unit,
      label: `— vs ${tripLabel}`,
      chip: "Burn vs plan —",
      tooltip,
      tripLabel,
      actualLabel: "—",
      deltaLabel: "—",
      heading: input.heading ?? "Burn vs plan",
    };
  }
  return {
    status: "compared",
    unit,
    label: `${actualLabel} vs ${tripLabel}`,
    chip: `Burn vs plan ${deltaLabel}`,
    tooltip,
    tripLabel,
    actualLabel,
    deltaLabel,
    heading: input.heading ?? "Burn vs plan",
  };
}

export function fuelInUnit(lb: number, unit: FuelUnit, fuel: FuelKind = "jeta"): number {
  return convertFuel(lb, "lb", unit, fuel);
}
